import { get, post, put } from "aws-amplify/api";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import OwnerPageGeneralInfo from "containers/owner/page/OwnerPageGeneralInfo";
import OwnerPageIndexModal from "containers/owner/page/OwnerPageIndexModal";
import OwnerPageOriginModal from "containers/owner/page/OwnerPageOriginModal";
import OwnerPageSearchModal from "containers/owner/page/OwnerPageSearchModal";
import OwnerPageTreeModal from "containers/owner/page/OwnerPageTreeModal";
import 'bootstrap/dist/css/bootstrap.min.css';
import OwnerPagePropertyOwnerList from "containers/owner/page/OwnerPagePropertyOwnerList";
import OwnerPageOccupantsList from "containers/owner/page/OwnerPageOccupantsList";
import RecordHistory from "containers/common/RecordHistory";
import { Alert, Button, Card, Col, Form, Modal, Nav, Row, Tab, Tabs, ListGroup } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { PacmanLoader } from "react-spinners";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory } from "utils/elementIds";

const initialOwner = {
    // Primary Identifiers
    owner_id: "new",
    origin_owner_id: null,
    origin_db: null,
    owner_name: '',
    is_company: false,

    // Name & Entity info
    date_last_search: '',
    date_next_search: '',
    acn: '',
    dir_name_1: '',
    dir_name_2: '',
    search_notes: '',

    // Physical Address
    exclude_from_mailings: false,
    care_of: '',
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',

    // Postal Address
    p_exclude_from_mailings: false,
    p_care_of: '',
    p_address: '',
    p_po_box: '',
    p_suburb: '',
    p_state: '',
    p_postcode: '',
    p_country: '',

    // Contact Details
    telephone: '',
    fax: '',
    website: '',
    email: '',
    contact_salutation: '',
    contact_first_name: '',
    contact_middle_initial: '',
    contact_surname: '',
    contact_position: '',
    contact_email: '',
    contact_telephone: '',
    contact_notes: '',

    //Owner Text
    owner_text: '',
    alternate_text: '',

    //General Notes
    notes: '',

    //Selected Options
    selectedPropertyOwnerIndex: null,
    selectedSuburb: null,
    selectedPostalSuburb: null,
    selectedSalutation: null,
    selectedPosition: null,
    selectedCountryFreetext: null,
    selectedPostalCountryFreetext: null,

    // System fields
    modified: false,
    created_by: null,
    created_date: null,
    last_edited_by: null,
    last_edited_date: null,
}

let cachedSuburbOptions = null;
let suburbsRequestPromise = null;

const normalizeSuburbOptions = (items = []) => {
    const source = Array.isArray(items) ? items : [];

    return source
        .map((item) => {
            if (!item) {
                return null;
            }

            if (typeof item === "string") {
                const text = item.trim();
                return text ? { value: text, label: text } : null;
            }

            const value = String(item.value ?? item.suburb ?? item.label ?? "").trim();
            if (!value) {
                return null;
            }

            return {
                ...item,
                value,
                label: String(item.label ?? item.suburb ?? value),
                state: item.state,
                postcode: item.postcode ?? item.post_code,
            };
        })
        .filter(Boolean);
};

const normalizeOwnerFromApi = (owner = {}) => {
    const normalizedOwner = { ...initialOwner, ...owner };

    if ((!normalizedOwner.p_po_box || normalizedOwner.p_po_box === '') && normalizedOwner.p_address) {
        normalizedOwner.p_po_box = normalizedOwner.p_address;
    }

    if ((!normalizedOwner.website || normalizedOwner.website === '') && normalizedOwner.internet) {
        normalizedOwner.website = normalizedOwner.internet;
    }

    return normalizedOwner;
};

const normalizeOwnerForApi = (owner = {}) => ({
    ...owner,
    p_address: owner.p_po_box ?? owner.p_address ?? '',
    internet: owner.website ?? owner.internet ?? '',
});

const areFieldValuesEqual = (left, right) => {
    if (left instanceof Date && right instanceof Date) {
        return left.getTime() === right.getTime();
    }

    return left === right;
};

function OwnerPageDetail({
    isAuthenticated,
    user,
    firstNames,
    salutationOptions,
    positionOptions,
    countryOptions,
    countryCodeOptions,
    suburbOptions,
    owner_id_parent,
    validateRelatedRecord,
    deleteFile,
}) {
    if ((!cachedSuburbOptions || cachedSuburbOptions.length === 0) && Array.isArray(suburbOptions) && suburbOptions.length > 0) {
        cachedSuburbOptions = normalizeSuburbOptions(suburbOptions);
    }

    const normalizeIdList = useCallback((ids) => {
        if (!Array.isArray(ids)) {
            return [];
        }

        const seen = new Set();
        const normalized = [];

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];

            if (id === null || id === undefined || id === "") {
                continue;
            }

            const idString = String(id);
            if (!seen.has(idString)) {
                seen.add(idString);
                normalized.push(idString);
            }
        }

        return normalized;
    }, []);

    const location = useLocation();
    const params = useParams();
    const { id } = useParams();
    const navigate = useNavigate();

    const [previousId, setPreviousId] = useState(null);
    const [nextId, setNextId] = useState(null);
    const [currentIdIndex, setCurrentIdIndex] = useState(null);
    const [pageInput, setPageInput] = useState("");
    const [isBrowseMultiSelect, setIsBrowseMultiSelect] = useState(false);
    const [owner_id_child] = useState(null);
    const [ownerData, setOwnerData] = useState(initialOwner);
    const {
        owner_id,
        created_by,
        created_date,
        last_edited_by,
        last_edited_date,
    } = ownerData;
    const ownerFilter = useMemo(() => ({ ownerid: owner_id || id }), [owner_id, id]);
    const [ui, setUi] = useState({
        isLoading: params.id !== 'new',
        isIndexLoading: false,
        isDeleting: false,
        showAddressSelect: false,
        showOwnerIndexDialog: false,
        showPropertyOwnerDialog: false,
        showOwnerOriginDialog: false,
        showOwnerTreeDialog: false,
        showOwnerTreeChildDialog: false,
        showOwnerSearchDialog: false,
        showDirectors: false,
        showTextModal: false,
        showAlert: false,
        errorMessage: ""
    });
    const [collections, setCollections] = useState({
        index: [],
        origins: [],
        tree: [],
        files: [],
        occupants: [],
        suburbOptions: cachedSuburbOptions || normalizeSuburbOptions(suburbOptions),
        positionOptions: positionOptions,
        idList: [],
        ownerTree: [],
        owner: [],
        propertyowners: [],
        treeChild: [],
        propertyidlist: [],
        countryOptions: countryOptions,
        salutationOptions: salutationOptions,
        countryCodeOptions: countryCodeOptions,
    });
    const latestImmediateSaveIdRef = useRef(0);
    const persistedOwnerRef = useRef(initialOwner);
    const pendingBlurSaveRef = useRef(null);
    const blurSaveTimeoutRef = useRef(null);
    const isImmediateSaveInFlightRef = useRef(false);
    const createId = useMemo(() => createElementIdFactory("owner-page-detail"), []);

    const runOwnerValidation = useCallback((record) => {
        if (typeof validateRelatedRecord !== "function") {
            return [true, ""];
        }

        const validationResult = validateRelatedRecord(record, "owner");

        if (Array.isArray(validationResult)) {
            const [isValid = true, message = ""] = validationResult;
            return [Boolean(isValid), message || ""];
        }

        if (validationResult && typeof validationResult === "object") {
            const isValid = validationResult.isValid !== undefined
                ? Boolean(validationResult.isValid)
                : true;
            const message = validationResult.validationMessage || validationResult.message || "";
            return [isValid, message];
        }

        return [true, ""];
    }, [validateRelatedRecord]);

    // --- Data Fetching and Update Functions ---
    const getOwner = useCallback(async (id, filter = {}) => {
        const actualId = typeof id === 'object' ? id.current : id;

        if (!actualId || actualId === "new") {
            return [];
        }

        const path = `/owners/${id}`;

        try {
            const response = await get({
                apiName: "colin",
                path: path,
                options: {
                    queryParams: filter,
                }
            }).response;

            const localData = await response.body.json();
            return normalizeOwnerFromApi(localData);

        } catch (e) {
            console.error("Error fetching owner:", e);
            return [];
        }
    }, []);

    const getSuburbs = useCallback(async () => {
        if (Array.isArray(cachedSuburbOptions) && cachedSuburbOptions.length > 0) {
            return cachedSuburbOptions;
        }

        if (suburbsRequestPromise) {
            return suburbsRequestPromise;
        }

        suburbsRequestPromise = (async () => {
            try {
                const response = await get({
                    apiName: "colin",
                    path: `/suburbs`,
                }).response;

                const payload = await response.body.json();
                const normalized = normalizeSuburbOptions(payload);
                cachedSuburbOptions = normalized;
                return normalized;
            } catch (error) {
                console.error("Error fetching suburbs:", error);
                return [];
            } finally {
                suburbsRequestPromise = null;
            }
        })();

        return suburbsRequestPromise;
    }, []);



    const getOwnerTree = useCallback(async (filter = {}) => {
        const currentId = params.id;
        if (!currentId || currentId === "new") return [];

        try {
            const { response } = await get({
                apiName: "colin",
                path: `/owners/${currentId}/tree`,
                options: { queryParams: filter }
            });

            const responseData = await response;
            if (responseData.body) {
                return await responseData.body.json();
            }
            return [];
        } catch (e) {
            console.error("Error fetching owner tree:", e);
            return [];
        }
    }, [params.id]);

    const saveOwnerAPI = useCallback(async (data) => {
        const isNew = data.owner_id === "new";
        const path = isNew ? "/owners/new" : `/owners/${data.owner_id}`;
        const method = isNew ? post : put;
        const payload = normalizeOwnerForApi(data);

        try {
            const response = await method({
                apiName: "colin",
                path: path,
                options: { body: payload }
            }).response;

            const result = await response.body.json();
            const apiSucceeded = result && result.result !== false;

            if (!apiSucceeded) {
                const apiErrorMessage = result?.error_message || result?.message || "Save failed";
                throw new Error(apiErrorMessage);
            }

            const resolvedOwnerId = result?.owner_id || result?.id || result?.ownerid || null;
            const normalizedResult = resolvedOwnerId
                ? { ...result, owner_id: resolvedOwnerId }
                : result;
            const mergedResult = {
                ...data,
                ...normalizedResult,
            };

            if (isNew && resolvedOwnerId) {
                navigate(`/owners/${resolvedOwnerId}`, { replace: true });
            }

            return normalizeOwnerFromApi(mergedResult);
        } catch (error) {
            console.error("API Save Error:", error);
            throw error;
        }
    }, [navigate]);

    const handleCloseAlert = () => {
        setUi(prev => ({ ...prev, showAlert: false }));

        // For a failed create flow (/owners/new), clear fields so the user can retry cleanly.
        if (ownerData?.owner_id === "new") {
            setOwnerData({ ...initialOwner });
        }
    };

    const saveRecord = async (record, dataSet, idKey) => {
        const isNew = !record[idKey] || String(record[idKey]).includes('temp') || record.new_record === true;

        try {
            const method = isNew ? post : put;

            const isPropertyOwnerCreate =
                dataSet === "propertyowners" &&
                isNew;

            const isPropertyOwnerUpdate =
                dataSet === "propertyowners" &&
                !isNew;

            const path = isPropertyOwnerCreate
                ? `/propertyowners/new`
                : isPropertyOwnerUpdate
                    ? `/propertyowners/${record[idKey]}`
                : isNew
                    ? `/owners/${ownerData.owner_id}/${dataSet}/new`
                    : `/owners/${ownerData.owner_id}/${dataSet}/${record[idKey]}`;

            const effectiveOwnerId =
                (record.owner_id && record.owner_id !== "new" && record.owner_id) ||
                (ownerData.owner_id && ownerData.owner_id !== "new" && ownerData.owner_id) ||
                (params.id && params.id !== "new" && params.id) ||
                null;

            const payload = path.startsWith('/owners/')
                ? {
                    ...record,
                    ...(effectiveOwnerId ? { owner_id: effectiveOwnerId } : {})
                }
                : record;

            const { body } = await method({
                apiName: "colin",
                path: path,
                options: { body: payload }
            }).response;

            const result = await body.json();
            const apiSucceeded = result && result.result !== false;

            if (!apiSucceeded) {
                console.error("Save failed with API response:", result);
                const errorMessage = result?.error_message || "Save failed";
                throw new Error(errorMessage);
            }

            const resolvedId = result.id || result.subowner_id || result[idKey] || record[idKey];

            const savedItem = {
                ...record,
                ...result,
                [idKey]: resolvedId,
                new_record: false,
                modified: false
            };

            setCollections(prev => {
                const currentList = prev[dataSet] || [];
                const recordId = record[idKey];

                let matchIndex = -1;

                if (currentList.length > 0) {
                    for (let i = 0; i < currentList.length; i++) {
                        const item = currentList[i];
                        if (!item) continue;

                        if (item === record) {
                            matchIndex = i;
                            break;
                        }

                        const itemId = item[idKey];
                        if (recordId !== undefined && recordId !== null && recordId !== "") {
                            if (String(itemId) === String(recordId)) {
                                matchIndex = i;
                                break;
                            }
                        }

                        if (resolvedId !== undefined && resolvedId !== null && resolvedId !== "") {
                            if (String(itemId) === String(resolvedId)) {
                                matchIndex = i;
                                break;
                            }
                        }
                    }
                }

                let nextList;
                if (matchIndex >= 0) {
                    nextList = [...currentList];
                    nextList[matchIndex] = savedItem;
                } else {
                    nextList = [savedItem, ...currentList];
                }

                if (resolvedId !== undefined && resolvedId !== null && resolvedId !== "") {
                    const seen = new Set();
                    nextList = nextList.filter(item => {
                        const itemId = item?.[idKey];
                        if (itemId === undefined || itemId === null || itemId === "") {
                            return true;
                        }

                        const key = String(itemId);
                        if (seen.has(key)) {
                            return false;
                        }

                        seen.add(key);
                        return true;
                    });
                }

                return {
                    ...prev,
                    [dataSet]: nextList
                };
            });

            return savedItem;

        } catch (error) {
            console.error("Save failed:", error);
            throw error;
        }
    };

    // --- Effect Hook --
    const loadAllOwnerData = useCallback(async () => {
        setUi(prev => ({ ...prev, isLoading: true }));

        try {
            const [
                localOwner,
                localSuburbs,
            ] = await Promise.all([
                getOwner(params.id),
                getSuburbs(),
            ]);

            const {
                tree,
                occupants,
                index,
                origins,
                propertyowners,
                tree_child,
            } = localOwner;

            setOwnerData(prev => ({ ...initialOwner, ...localOwner }));
            persistedOwnerRef.current = { ...initialOwner, ...localOwner };
            setCollections(prev => ({
                ...prev,
                suburbOptions: localSuburbs,
                tree: tree || [],
                occupants: occupants || [],
                index: index || [],
                origins: origins || [],
                treeChild: tree_child || [],
                propertyowners: propertyowners || [],
            }));

        } catch (error) {
            setUi(prev => ({ ...prev, errorMessage: "Failed to load owner data", showAlert: true }));
        } finally {
            setUi(prev => ({ ...prev, isLoading: false }));
        }
    }, [params.id, getOwner, getSuburbs]);

    useEffect(() => {
        if (isAuthenticated) {
            loadAllOwnerData();

            if (isBrowseMultiSelect && collections.idList && collections.idList.length > 0) {
                const currentOwnerId = String(params.id);
                const currentIndex = collections.idList.findIndex(item => String(item) === currentOwnerId);

                if (currentIndex !== -1) {
                    setCurrentIdIndex(currentIndex);
                    setPreviousId(currentIndex > 0 ? collections.idList[currentIndex - 1] : null);
                    setNextId(currentIndex < collections.idList.length - 1 ? collections.idList[currentIndex + 1] : null);
                }
            } else {
                setCurrentIdIndex(null);
                setPreviousId(null);
                setNextId(null);
            }
        }
    }, [isAuthenticated, loadAllOwnerData, params.id, collections.idList, isBrowseMultiSelect]);

    useEffect(() => {
        if (location.state && location.state.data) {
            const normalizedIds = normalizeIdList(location.state.data);
            const browseFromMultiSelect = Boolean(location.state.isBrowseMultiSelect && normalizedIds.length > 1);

            setCollections(prev => ({
                ...prev,
                idList: normalizedIds,
                propertyidlist: location.state.propertyidlist || []
            }));
            setIsBrowseMultiSelect(browseFromMultiSelect);
        } else {
            setIsBrowseMultiSelect(false);
        }
    }, [location.state, normalizeIdList]);

    // --- Toggle Handlers ---
    const toggleOwnerPageIndexModal = async () => {
        const willBeOpen = !ui.showOwnerIndexDialog;

        if (!willBeOpen) {
            setCollections(prev => ({
                ...prev,
                index: (prev.index || []).filter(record => {
                    const isTemp = record?.new_record || String(record?.owner_index_id || "").includes("temp-");
                    const hasName = Boolean((record?.owner_name || "").trim());
                    return !(isTemp && !hasName);
                })
            }));

            try {
                if (ownerData.owner_id && ownerData.owner_id !== "new") {
                    const restOperation = get({
                        apiName: "colin",
                        path: `/owners/${ownerData.owner_id}/index`
                    });

                    const { body } = await restOperation.response;
                    const responseData = await body.json();

                    setCollections(prev => ({
                        ...prev,
                        index: Array.isArray(responseData) ? responseData : []
                    }));
                }
            } catch (error) {
                console.error("Error refreshing owner index on modal close:", error);
            }
        }

        setUi(prev => ({
            ...prev,
            showOwnerIndexDialog: willBeOpen
        }));

        if (willBeOpen) {
            try {
                if (!ownerData.owner_id || ownerData.owner_id === "new") return;

                const restOperation = get({
                    apiName: "colin",
                    path: `/owners/${ownerData.owner_id}/index`
                });

                const { body } = await restOperation.response;
                const responseData = await body.json();

                setCollections(prev => ({
                    ...prev,
                    index: Array.isArray(responseData) ? responseData : []
                }));

            } catch (error) {
                console.error("Error getting owner index:", error);
                setCollections(prev => ({ ...prev, index: [] }));
            }
        }
    };

    const toggleOwnerPageOriginModal = async () => {
        const willBeOpen = !ui.showOwnerOriginDialog;

        if (!willBeOpen) {
            try {
                if (ownerData.owner_id && ownerData.owner_id !== "new") {
                    const restOperation = get({
                        apiName: "colin",
                        path: `/owners/${ownerData.owner_id}/origins`
                    });

                    const { body } = await restOperation.response;
                    const responseData = await body.json();

                    setCollections(prev => ({
                        ...prev,
                        origins: Array.isArray(responseData) ? responseData : []
                    }));
                }
            } catch (error) {
                console.error("Error refreshing owner origins on modal close:", error);
            }
        }

        setUi(prev => ({
            ...prev,
            showOwnerOriginDialog: willBeOpen
        }));

        if (willBeOpen) {
            try {
                if (!ownerData.owner_id || ownerData.owner_id === "new") {
                    return;
                }

                const restOperation = get({
                    apiName: "colin",
                    path: `/owners/${ownerData.owner_id}/origins`
                });

                const { body } = await restOperation.response;
                const responseData = await body.json();

                setCollections(prev => ({
                    ...prev,
                    origins: Array.isArray(responseData) ? responseData : []
                }));

            } catch (error) {
                console.error("Error getting owner origins:", error);
                setCollections(prev => ({ ...prev, origins: [] }));
            }
        }
    };

    const toggleOwnerPageSearchModal = () => {
        setUi(prev => ({
            ...prev,
            showOwnerSearchDialog: !prev.showOwnerSearchDialog
        }));
    };

    const handleOwnerParentAdded = useCallback(async () => {
        try {
            const localTree = await getOwnerTree({ ownerid: owner_id });
            setCollections(prev => ({
                ...prev,
                tree: Array.isArray(localTree) ? localTree : []
            }));
        } catch (error) {
            console.error("Failed to refresh owner tree after adding parent:", error);
        }
    }, [getOwnerTree, owner_id]);

    const togglePropertyOwnerModal = () => {
        setUi(prev => ({ ...prev, showPropertyOwnerDialog: !prev.showPropertyOwnerDialog }));
        handleRefreshProperties();
    };

    const toggleOwnerTextModal = () => {
        setUi(prev => ({ ...prev, showTextModal: !prev.showTextModal }));
    };

    const toggleOwnerPageTreeModal = () => {
        setUi(prev => ({ ...prev, showOwnerTreeDialog: !prev.showOwnerTreeDialog }));
    }

    const toggleOwnerPageTreeChildModal = () => {
        setUi(prev => ({ ...prev, showOwnerTreeChildDialog: !prev.showOwnerTreeChildDialog }));
    }

    // --- Input Change Handlers ---
    const handleChange = (e, customName = null) => {
        let name, value;
        if (!e || !e.target) {
            name = customName;
            value = e;
        } else {
            const { name: targetName, value: targetValue, type, checked } = e.target;
            name = targetName;
            value = type === 'checkbox' ? checked : targetValue;
        }

        const persistedValue = persistedOwnerRef.current?.[name];
        const valueChanged = String(value ?? "") !== String(persistedValue ?? "");

        if (ui.showAlert) {
            const isNewOwner = String(ownerData?.owner_id || "") === "new";
            const isRequiredField = name === "origin_owner_id" || name === "owner_name";
            const valueIsValid = isRequiredField
                ? String(value ?? "").trim().length > 0
                : true;

            if (!isNewOwner || !isRequiredField || valueIsValid) {
                setUi(prev => ({ ...prev, showAlert: false, errorMessage: "" }));
            }
        }

        if (name === "suburb" && value?.value) {
            setOwnerData(prev => ({
                ...prev,
                suburb: value.value,
                state: value.state || prev.state,
                post_code: value.postcode || prev.post_code,
                modified: valueChanged
            }));
        } else {
            setOwnerData(prev => ({
                ...prev,
                [name]: value,
                modified: valueChanged
            }));
        }
    };

    const getRequiredFieldValidationMessage = useCallback((data) => {
        if (String(data?.owner_id || "") !== "new") {
            return "";
        }

        const missingFields = [];
        if (!String(data?.origin_owner_id || "").trim()) {
            missingFields.push("Key");
        }

        if (!String(data?.owner_name || "").trim()) {
            missingFields.push("Name");
        }

        if (missingFields.length === 0) {
            return "";
        }

        return `Please enter ${missingFields.join(" and ")} before saving.`;
    }, []);

    const handleImmediateSave = useCallback(async (updatedData) => {
        const requiredFieldMessage = getRequiredFieldValidationMessage(updatedData);
        const [isValid, validationMessage] = runOwnerValidation(updatedData);

        if (requiredFieldMessage || !isValid) {
            setOwnerData(prev => ({ ...prev, ...updatedData, modified: true }));
            setUi(prev => ({
                ...prev,
                errorMessage: validationMessage || requiredFieldMessage,
                showAlert: true
            }));
            return false;
        }

        const saveId = latestImmediateSaveIdRef.current + 1;
        latestImmediateSaveIdRef.current = saveId;

        // Non-visual fields: changes to these should never trigger a UI repaint
        const NON_VISUAL_FIELDS = new Set([
            'modified', 'selectedPropertyOwnerIndex', 'selectedSuburb',
            'selectedPostalSuburb', 'selectedSalutation', 'selectedPosition',
            'selectedCountryFreetext', 'selectedPostalCountryFreetext',
        ]);

        try {
            isImmediateSaveInFlightRef.current = true;
            const result = await saveOwnerAPI(updatedData);

            if (latestImmediateSaveIdRef.current === saveId) {
                // Only apply fields whose value the server actually changed from what we sent.
                // Comparing against updatedData (sent) rather than current state means we never
                // overwrite a newer user edit and never re-render when server echoes the same value.
                const serverChanges = {};
                for (const key of Object.keys(updatedData)) {
                    if (NON_VISUAL_FIELDS.has(key)) continue;
                    if (!(key in result)) continue;
                    if (!areFieldValuesEqual(updatedData[key], result[key])) {
                        serverChanges[key] = result[key];
                    }
                }

                if (Object.keys(serverChanges).length > 0) {
                    setOwnerData(prev => ({ ...prev, ...serverChanges }));
                }
                // Keep the persisted ref in sync so future blur comparisons are correct
                persistedOwnerRef.current = { ...persistedOwnerRef.current, ...updatedData, ...serverChanges, modified: false };
            }
            return true;
        } catch (e) {
            if (latestImmediateSaveIdRef.current === saveId) {
                setUi(prev => ({ ...prev, errorMessage: e?.message || "Save failed", showAlert: true }));
            }
            return false;
        } finally {
            if (latestImmediateSaveIdRef.current === saveId) {
                isImmediateSaveInFlightRef.current = false;

                const pendingOwner = pendingBlurSaveRef.current;
                if (pendingOwner && !blurSaveTimeoutRef.current) {
                    pendingBlurSaveRef.current = null;
                    handleImmediateSave(pendingOwner);
                }
            }
        }
    }, [getRequiredFieldValidationMessage, runOwnerValidation, saveOwnerAPI]);

    const scheduleBlurSave = useCallback((nextOwnerData) => {
        pendingBlurSaveRef.current = nextOwnerData;

        if (blurSaveTimeoutRef.current) {
            clearTimeout(blurSaveTimeoutRef.current);
        }

        blurSaveTimeoutRef.current = setTimeout(() => {
            blurSaveTimeoutRef.current = null;

            if (isImmediateSaveInFlightRef.current) {
                return;
            }

            const pendingOwner = pendingBlurSaveRef.current;
            if (!pendingOwner) {
                return;
            }

            pendingBlurSaveRef.current = null;
            handleImmediateSave(pendingOwner);
        }, 180);
    }, [handleImmediateSave]);

    const handleFormBlurCapture = useCallback(async (event) => {
        const target = event?.target;
        if (!target || !target.name) {
            return;
        }

        const fieldName = target.name;
        if (!(fieldName in initialOwner)) {
            return;
        }

        const nextValue = target.type === "checkbox" ? Boolean(target.checked) : target.value;
        const isNewOwner = String(ownerData?.owner_id || "") === "new";

        const updatedOwner = {
            ...ownerData,
            [fieldName]: nextValue,
            modified: true,
        };

        if (isNewOwner) {
            const keyFilled = String(fieldName === "origin_owner_id" ? nextValue : ownerData.origin_owner_id || "").trim();
            const nameFilled = String(fieldName === "owner_name" ? nextValue : ownerData.owner_name || "").trim();

            if (isImmediateSaveInFlightRef.current) {
                pendingBlurSaveRef.current = updatedOwner;
                return;
            }

            if (!keyFilled || !nameFilled) {
                scheduleBlurSave(updatedOwner);
                return;
            }
        } else {
            const persistedValue = persistedOwnerRef.current?.[fieldName];
            if (String(nextValue ?? "") === String(persistedValue ?? "")) {
                return;
            }

            if (isImmediateSaveInFlightRef.current) {
                pendingBlurSaveRef.current = updatedOwner;
                return;
            }
        }

        scheduleBlurSave(updatedOwner);
    }, [ownerData, scheduleBlurSave]);

    useEffect(() => {
        return () => {
            if (blurSaveTimeoutRef.current) {
                clearTimeout(blurSaveTimeoutRef.current);
            }
        };
    }, []);

    const handleBlur = async () => {
        setUi(prevUi => ({ ...prevUi, showAlert: false }));

        if (!ownerData.modified) return;

        const updatedOwner = {
            ...ownerData,
            last_edited_by: user.name,
        };

        const [isValid, validationMessage] = runOwnerValidation(updatedOwner);

        if (!isValid) {
            setUi(prevUi => ({
                ...prevUi,
                errorMessage: validationMessage,
                showAlert: true
            }));
            return;
        }

        try {
            setOwnerData(prev => ({ ...prev, modified: false }));

            setUi(prevUi => ({ ...prevUi, isSaving: true }));

            // if (result.result === false) {
            //     alert(result.error_message);
            //     return;
            // }

            // if (result.error_message !== null && result.error_message !== String(ownerData.is_company)) {
            //     const isCo = result.error_message === "true";
            //     setOwnerData(prev => ({ ...prev, is_company: isCo }));
            //     setErrorMessage(`This owner has ${isCo ? "" : "NOT"} been automatically flagged as a company.`);
            //     setShowWarning(true);
            // }

            // if (file) {
            //     const attachment = await s3Upload(file, "asic");
            //     if (attachment) {
            //         const url = await _s3GetURL(attachment.replace("asic/", ""), 'asic');
            //         setFiles(prev => [...prev, { key: attachment, url: url }]);
            //     }
            // }

            if (id === "new") {
                // await addPropertyRelation(result.id);
                // navigate(`/owners/${result.id}`);
            }

            const result = await saveOwnerAPI(updatedOwner);

            const merged = { ...updatedOwner, ...result, modified: false };
            setOwnerData(prev => ({ ...prev, ...merged }));
            persistedOwnerRef.current = { ...merged };

        } catch (e) {
            setUi(prevUi => ({
                ...prevUi,
                errorMessage: "Save failed: " + e.message,
                showAlert: true
            }));
            setOwnerData(prev => ({ ...prev, modified: true }));
        } finally {
            setUi(prevUi => ({ ...prevUi, isSaving: false }));
        }
    };

    const handleNavigation = async (targetId) => {
        if (ownerData.modified) {
            await handleBlur();
        }

        navigate(`/owners/${targetId}`, {
            state: {
                data: collections.idList,
                propertyidlist: collections.propertyidlist,
                isBrowseMultiSelect,
            }
        });
    };

    const handleGoToRecord = async (e) => {
        const targetPage = parseInt(pageInput);
        const targetIndex = targetPage - 1;

        if (
            !isNaN(targetIndex) &&
            targetIndex >= 0 &&
            targetIndex < collections.idList.length &&
            targetIndex !== currentIdIndex
        ) {
            const targetId = collections.idList[targetIndex];
            await handleNavigation(targetId);
        } else {
            setPageInput(currentIdIndex + 1);
        }
    };

    async function handleRefreshProperties(key) {
        if (key === "1" || key === 1) {
            if (!owner_id || owner_id === 'new') {
                return;
            }

            try {
                const localTree = await getOwnerTree({ ownerid: owner_id });
                if (localTree) {
                    setCollections(prev => ({
                        ...prev,
                        tree: localTree
                    }));
                }
            } catch (err) {
                console.error("Failed to refresh tree:", err);
            }
        }
    }

    const handleCurrentIdIndexChange = (e) => {
        setPageInput(e.target.value);
    };

    const handleEnterCurrentIdIndex = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleGoToRecord(e);
        }
    };

    useEffect(() => {
        if (currentIdIndex !== null) {
            setPageInput(currentIdIndex + 1);
        }
    }, [currentIdIndex]);

    const handleAddStreetAddressFromProperty = (selectedProp = null) => {
        const prop = selectedProp || (collections.propertyowners && collections.propertyowners[0]);
        if (prop) {
            const updatedData = {
                ...ownerData,
                address_line_1: prop.street_address || "",
                suburb: prop.suburb || "",
                state: prop.state || "",
                postcode: prop.postcode || "",
                selected_suburb: { value: prop.suburb, label: prop.suburb },
                modified: true
            };

            setOwnerData(updatedData);
            handleImmediateSave(updatedData);
        }
    };

    const updateTableState = (indexOrNewData, columnIdOrDataSet, value, dataSet) => {
        if (Array.isArray(indexOrNewData)) {
            const targetKey = columnIdOrDataSet;

            setCollections(prev => {
                return {
                    ...prev,
                    [targetKey]: indexOrNewData
                };
            });
        }
        else {
            const rowIndex = indexOrNewData;
            const colId = columnIdOrDataSet;
            const target = dataSet;

            if (!target || !collections[target]) {
                console.error("DataSet missing or invalid:", target);
                return;
            }

            setCollections(prev => {
                const currentList = [...(prev[target] || [])];
                if (!currentList[rowIndex]) return prev;

                currentList[rowIndex] = {
                    ...currentList[rowIndex],
                    [colId]: value,
                    modified: true
                };

                return {
                    ...prev,
                    [target]: currentList
                };
            });
        }
    };

    const handleOwnerText = async (e) => {
        e?.preventDefault();
        setUi(prev => ({ ...prev, isIndexLoading: true }));

        try {
            const result = await get({
                apiName: "colin",
                path: `/owners/${owner_id}/text`
            }).response;

            if (result) {
                const response = await result.body.json();

                const textString = response[0]?.owner_text || "";

                setOwnerData(prev => ({
                    ...prev,
                    owner_text: textString
                }));

                setUi(prev => ({
                    ...prev,
                    showTextModal: true
                }));
            }
        } catch (error) {
            console.error("Error getting owner text:", error);
        } finally {
            setUi(prev => ({ ...prev, isIndexLoading: false }));
        }
    };

    const selectAddress = () => {
        setUi(prev => ({
            ...prev,
            showAddressSelect: !prev.showAddressSelect
        }));
    };

    // --- Render Functions ---
    function renderForm() {
        const availableProperties = Array.isArray(ownerData?.propertyowners) && ownerData.propertyowners.length > 0
            ? ownerData.propertyowners
            : (Array.isArray(collections?.propertyowners) ? collections.propertyowners : []);

        return (
            <div className="owner">
                {ui.showAddressSelect &&
                    <Modal show={ui.showAddressSelect} onHide={selectAddress} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Select a Property</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {availableProperties.length > 0 ? (
                                <ListGroup variant="flush">
                                    {availableProperties.map((property) => (
                                        <ListGroup.Item key={property.property_key || property.street_address}>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                className="text-start"
                                                onClick={() => {
                                                    handleAddStreetAddressFromProperty(property);
                                                    setUi(prev => ({
                                                        ...prev,
                                                        showAddressSelect: false
                                                    }));
                                                }}
                                            >
                                                Use - {property.property_key || "Unknown property"} {property.street_address || "No address available"}
                                            </Button>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <div className="text-muted">No properties found.</div>
                            )}
                        </Modal.Body>
                    </Modal>
                }

                <OwnerPageIndexModal
                    controlType="table"
                    show={ui.showOwnerIndexDialog}
                    onClose={toggleOwnerPageIndexModal}
                    user={user}
                    data={collections.index}
                    updateTableState={updateTableState}
                    saveRecord={saveRecord}
                    ownerid={owner_id}
                />

                <OwnerPageOriginModal
                    show={ui.showOwnerOriginDialog}
                    onClose={toggleOwnerPageOriginModal}
                    user={user}
                    data={collections.origins}
                    updateTableState={updateTableState}
                    saveRecord={saveRecord}
                    ownerid={owner_id}
                    countryCodeOptions={countryCodeOptions}
                />

                <OwnerPageTreeModal
                    show={ui.showOwnerTreeDialog}
                    onClose={toggleOwnerPageTreeModal}
                    user={user}
                    data={collections.tree}
                    updateTableState={updateTableState}
                    saveRecord={saveRecord}
                    ownerid={owner_id}
                    parentChild="subsidiary"
                />

                <OwnerPageTreeModal
                    show={ui.showOwnerTreeChildDialog}
                    onClose={toggleOwnerPageTreeChildModal}
                    user={user}
                    data={collections.treeChild}
                    updateTableState={updateTableState}
                    saveRecord={saveRecord}
                    ownerid={owner_id}
                    parentChild="parent"
                />

                <OwnerPageSearchModal
                    isAuthenticated={true}
                    user={user}
                    onClose={toggleOwnerPageSearchModal}
                    show={ui.showOwnerSearchDialog}
                    ownerid={owner_id}
                    existingOwnerParents={collections.tree}
                    onOwnerParentAdded={handleOwnerParentAdded}
                    controlType="table3"
                />

                {ui.showTextModal &&
                    <Modal show={ui.showTextModal} onHide={toggleOwnerTextModal} dialogClassName="modal-90w">
                        <Modal.Header closeButton>
                            <Modal.Title>Owner Text</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {ownerData.owner_text}
                        </Modal.Body>
                    </Modal>
                }

                {ownerData && (
                    <div className="OwnerForm">
                        {ui.showWarning &&
                            <Alert variant="warning">{ui.errorMessage}</Alert>
                        }
                                {ui.showAlert && (
                                    <Alert variant="danger" dismissible onClose={handleCloseAlert}>
                                        {ui.errorMessage || "Save failed"}
                                    </Alert>
                                )}
                        <Form onBlurCapture={handleFormBlurCapture} >
                            <Form.Group as={Row} className="mt-3">
                                <Col ><h6>Owner Details - {ownerData?.owner_name || 'New Owner'} </h6></Col>

                                <Col>
                                    <Nav className="justify-content-center align-items-center owner-nav-controls" >
                                        {isBrowseMultiSelect && previousId !== null && !owner_id_child && !owner_id_parent &&
                                            <Nav.Item  >
                                                <Link to={`/owners/${previousId}`} state={{ data: collections.idList, propertyidlist: collections.propertyidlist, isBrowseMultiSelect }} >
                                                    <Button type="button" variant="info" className="owner-detail-action-btn" size="sm" onClick={() => handleNavigation(previousId)} disabled={ui.isLoading}>
                                                        Previous
                                                    </Button></Link>
                                            </Nav.Item>}

                                        {isBrowseMultiSelect && nextId !== null && !owner_id_child && !owner_id_parent &&
                                            <Nav.Item  >
                                                <Link to={`/owners/${nextId}`} state={{ data: collections.idList, propertyidlist: collections.propertyidlist, isBrowseMultiSelect }} >
                                                    <Button type="button" variant="info" className="owner-detail-action-btn" size="sm" onClick={() => handleNavigation(nextId)} disabled={ui.isLoading}>
                                                        Next
                                                    </Button></Link>
                                            </Nav.Item>}
                                        {isBrowseMultiSelect && currentIdIndex !== null && !owner_id_child && !owner_id_parent && collections.idList.length > 1 &&
                                            <div className="owner-nav-page-controls" >
                                                <Form.Control
                                                    id={createId("page-nav-input")}
                                                    onChange={handleCurrentIdIndexChange}
                                                    value={pageInput}
                                                    as="input"
                                                    type="text"
                                                    min="0"
                                                    className="pageCount owner-nav-page-input"
                                                    onBlur={handleGoToRecord}
                                                    onKeyDown={handleEnterCurrentIdIndex}
                                                />
                                                <span className="owner-nav-page-total">of {collections?.idList?.length || 0}</span>
                                            </div>
                                        }
                                    </Nav>
                                </Col>

                                <Col>
                                    <div
                                        className="d-flex flex-wrap align-items-stretch justify-content-start justify-content-lg-end"
                                        style={{ gap: "0.4rem" }}
                                    >
                                        <Button
                                            type="button"
                                            variant="primary"
                                            size="sm"
                                            onClick={handleOwnerText}
                                        >
                                            Preview Owner Text
                                        </Button>
                                        <a target="_blank" rel="noopener noreferrer" href="/countries">
                                            <Button
                                                variant="primary"
                                                type="button"
                                                size="sm"
                                            >
                                                Add/Edit Countries
                                            </Button>
                                        </a>
                                        <a target="_blank" rel="noopener noreferrer" href="/salutations">
                                            <Button
                                                variant="primary"
                                                type="button"
                                                size="sm"
                                            >
                                                Add/Edit Salutations
                                            </Button>
                                        </a>
                                        <a target="_blank" rel="noopener noreferrer" href="/positions">
                                            <Button
                                                variant="primary"
                                                type="button"
                                                size="sm"
                                            >
                                                Add/Edit Positions
                                            </Button>
                                        </a>
                                    </div>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} id={createId("detail")}>
                                <Col>
                                    <Tabs defaultActiveKey={0} id={createId("tabset")} onSelect={handleRefreshProperties}>

                                        <Tab eventKey={0} title="Owner Detail" >
                                            <Card id={createId("owner-details") }  >
                                                <Card.Body>
                                                    <OwnerPageGeneralInfo
                                                        ownerData={ownerData}
                                                        collections={collections}
                                                        handleChange={handleChange}
                                                        handleBlur={handleBlur}
                                                        handleImmediateSave={handleImmediateSave}
                                                        toggleOwnerPageIndexModal={toggleOwnerPageIndexModal}
                                                        toggleOwnerPageOriginModal={toggleOwnerPageOriginModal}
                                                        toggleOwnerPageTreeModal={toggleOwnerPageTreeModal}
                                                        toggleOwnerPageSearchModal={toggleOwnerPageSearchModal}
                                                        toggleOwnerPageTreeChildModal={toggleOwnerPageTreeChildModal}
                                                        setUi={setUi}
                                                        openAddressSelect={selectAddress}
                                                        saveRecord={saveRecord}
                                                        user={user}
                                                        updateTableState={updateTableState}
                                                    />
                                                </Card.Body>
                                            </Card >
                                        </Tab>

                                        <Tab eventKey={1} title="Properties">
                                            <div className="mt-3 text-muted" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                                                If you are adding this Owner to or removing this Owner from a Property,
                                                please be sure to click through and Preview the Owner Text for that Property.
                                            </div>
                                            <OwnerPagePropertyOwnerList
                                                show={ui.showPropertyOwnerDialog}
                                                onClose={togglePropertyOwnerModal}
                                                filter={ownerFilter}
                                                isAuthenticated={isAuthenticated}
                                                togglePropertyOwnerModal={togglePropertyOwnerModal}
                                                user={user}
                                                data={collections.propertyowners}
                                                updateTableState={updateTableState}
                                                saveRecord={saveRecord}
                                                ownerid={owner_id}
                                                controlType="table1"
                                                propertyidlist={(collections.propertyidlist || [])}
                                            />
                                        </Tab>

                                        <Tab eventKey={2} title="Occupants">
                                            <Card className="entity-detail-tab-card">
                                                <Card.Body>
                                                    <OwnerPageOccupantsList
                                                        user={user}
                                                        data={collections.occupants}
                                                        updateTableState={updateTableState}
                                                        saveRecord={saveRecord}
                                                        controlType="table"
                                                    />
                                                </Card.Body>
                                            </Card>
                                        </Tab>

                                        <Tab eventKey={3} title="Record History">
                                            <RecordHistory
                                                created_date={created_date}
                                                created_by={created_by}
                                                last_edited_date={last_edited_date}
                                                last_edited_by={last_edited_by}
                                            />
                                        </Tab>
                                    </Tabs>
                                </Col>
                            </Form.Group>
                        </Form>
                    </div>
                )}
            </div>
        );
    };

    function renderComponent() {
        return (
            <div className="componentRendering">
                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {ui.isLoading && (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color={"#e03c31"}
                                        loading={ui.isLoading}
                                    />
                                </div>
                            </div>
                        )}
                        {!ui.isLoading && ownerData
                            ? renderForm()
                            : null
                        }
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="OwnerPageDetail">
            {isAuthenticated ? renderComponent() : null}
        </div>
    );
}

export default OwnerPageDetail;